import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { requirementAPI, clientAPI, teamAPI, emailAPI } from '../utils/api';
import usePersistentState, { readPersisted } from '../utils/usePersistentState';
import { authorOptions } from '../utils/authorOptions';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button, Input, Select, Textarea, Modal, Badge, EmptyState, Pagination, Spinner, statusVariant, SearchableSelect, ConfirmModal} from '../components/UI';
import RichTextEditor from '../components/RichTextEditor';
import SafeHtml from '../components/SafeHtml';
import HistoryPanel from '../components/HistoryPanel';
import GlobalHistoryPanel from '../components/GlobalHistoryPanel';
import { fmtLPA } from '../utils/format';

// Numeric fields are rendered as text inputs (never number inputs) so mouse-wheel
// scroll can't silently change the value. These keep only valid numeric chars.
const onlyDigits  = (v) => String(v ?? '').replace(/[^0-9]/g, '');
const onlyDecimal = (v) => String(v ?? '').replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

const emptyForm = {
  jobTitle: '', jobDescription: '', requiredSkills: '',
  minExperience: '', maxExperience: '', clientId: '',
  budgetMin: '', budgetMax: '', budgetCurrency: 'USD', location: '', jobType: 'Full-time', workMode: 'On-site',
  status: 'Open', priority: 'Medium', openings: 1,
  billingCost: '', billingCurrency: 'USD', billingType: 'Fixed',
  contacts: [], teamId: '',
  description: '', candidateLocation: '', jobLocation: '', closeBy: '',
  shiftTime: '',
  batchTemplateId: '',
  defaultCcEmail: '',
  clientOfficeAddress: '',
  forceInterviewEmail: false,
};

const BILLING_TYPES = ['Fixed', 'Hourly', 'Monthly', 'Percentage'];
const CURRENCIES    = ['USD', 'INR', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD', 'JPY'];
const WORK_MODES    = ['On-site', 'Remote', 'Hybrid'];
const JOB_TYPES     = ['Full-time', 'Part-time', 'Contract', 'Freelance'];

const formatBilling = (cost, currency, type) => {
  if (!cost) return null;
  const sym = { USD: '$', EUR: '€', GBP: '£', INR: '₹', AED: 'AED ', SGD: 'S$', AUD: 'A$', CAD: 'C$', JPY: '¥', MYR: 'RM ', QAR: 'QAR ', SAR: 'SAR ' }[currency] || `${currency} `;
  const suffix = type === 'Hourly' ? '/hr' : type === 'Monthly' ? '/mo' : type === 'Percentage' ? '%' : '';
  const amount = type === 'Percentage' ? cost : `${sym}${Number(cost).toLocaleString()}`;
  return `${amount}${suffix}`;
};

const emptyContact = { name: '', email: '', bcc: false };

// ── Email tag input (chip-style) ──────────────────────────────────────────────
// Stores value as a comma-separated string to match the DB schema.
// Enter or comma commits a tag; Backspace on empty input removes the last.
function EmailTagInput({ value = '', onChange, placeholder, isDark }) {
  const tags = value ? value.split(',').map(e => e.trim()).filter(Boolean) : [];
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  const addTag = (raw) => {
    const email = raw.trim().toLowerCase();
    if (!email) return;
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValid) return;
    if (tags.includes(email)) { setInput(''); return; }
    onChange([...tags, email].join(','));
    setInput('');
  };

  const removeTag = (idx) => {
    const next = tags.filter((_, i) => i !== idx);
    onChange(next.join(','));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(input); }
    if (e.key === 'Backspace' && !input && tags.length) removeTag(tags.length - 1);
  };

  const base = {
    fontSize: 13,
    background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
    border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#c7d2fe'}`,
    color: isDark ? '#f1f5f9' : '#111827',
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      style={{ ...base, borderRadius: 8, padding: '6px 10px', display: 'flex', flexWrap: 'wrap', gap: 5, cursor: 'text', minHeight: 40, alignItems: 'center' }}
    >
      {tags.map((tag, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px 2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff', color: isDark ? '#a5b4fc' : '#4338ca', border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}` }}>
          {tag}
          <span onClick={e => { e.stopPropagation(); removeTag(i); }} style={{ cursor: 'pointer', fontSize: 13, lineHeight: 1, color: isDark ? '#818cf8' : '#6366f1', marginLeft: 1 }}>×</span>
        </span>
      ))}
      <input
        ref={inputRef}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => addTag(input)}
        placeholder={tags.length === 0 ? (placeholder || 'Type email and press Enter…') : ''}
        style={{ flex: 1, minWidth: 160, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: isDark ? '#f1f5f9' : '#111827' }}
      />
    </div>
  );
}

// ── Contact row component ─────────────────────────────────────────────────────
function ContactRow({ contact, index, onChange, onRemove }) {
  const inputStyle = {
    background: 'var(--bg-input)', border: '1px solid var(--border-input)',
    color: 'var(--text-primary)', borderRadius: 8, padding: '8px 12px', fontSize: 13,
    outline: 'none', width: '100%',
  };
  return (
    <div className="flex gap-2 items-center">
      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          value={contact.name}
          onChange={e => onChange(index, 'name', e.target.value)}
          placeholder="Person name"
          style={inputStyle}
          onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
          onBlur={e => { e.target.style.borderColor = 'var(--border-input)'; e.target.style.boxShadow = 'none'; }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <input
            value={contact.email}
            onChange={e => onChange(index, 'email', e.target.value)}
            placeholder="Email address"
            type="email"
            style={{ ...inputStyle, ...(contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contact.email) ? { borderColor: '#ef4444' } : {}) }}
            onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
            onBlur={e => { e.target.style.borderColor = contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contact.email) ? '#ef4444' : 'var(--border-input)'; e.target.style.boxShadow = 'none'; }}
          />
          {contact.email && !contact.email.includes('@') && <span style={{ color: '#ef4444', fontSize: 11, marginTop: 3 }}>Email must include @</span>}
          {contact.email && contact.email.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contact.email) && <span style={{ color: '#ef4444', fontSize: 11, marginTop: 3 }}>Enter a valid email (e.g. user@domain.com)</span>}
        </div>
      </div>
      {/* Per-contact BCC toggle — default OFF. When ON, this contact's email is
          auto-BCC'd on candidate emails for this requirement. */}
      <button
        type="button"
        role="switch"
        aria-checked={!!contact.bcc}
        onClick={() => onChange(index, 'bcc', !contact.bcc)}
        title={contact.bcc ? 'This contact is BCC’d on candidate emails' : 'Enable to BCC this contact on candidate emails'}
        style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 500, color: contact.bcc ? '#6366f1' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>BCC</span>
        <span style={{
          position: 'relative', width: 34, height: 20, borderRadius: 999,
          background: contact.bcc ? '#6366f1' : (typeof window !== 'undefined' ? 'var(--border-input)' : '#cbd5e1'),
          transition: 'background 0.15s ease', flexShrink: 0,
        }}>
          <span style={{
            position: 'absolute', top: 2, left: contact.bcc ? 16 : 2, width: 16, height: 16,
            borderRadius: '50%', background: '#fff', transition: 'left 0.15s ease',
            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
          }} />
        </span>
      </button>
      <Button
        type="button"
        variant="danger"
        size="sm"
        iconOnly
        onClick={() => onRemove(index)}
        style={{ flexShrink: 0, background: 'rgba(239,68,68,0.1)', color: '#ef4444', boxShadow: 'none' }}
      >✕</Button>
    </div>
  );
}

export default function Requirements() {
  const { isSuperAdmin, hasRole, isDeveloper, user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate  = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled  = useRef(false);
  const originalFormSnap = useRef(null); // snapshot of form when edit modal opens
  const canCreate = hasRole(['SuperAdmin', 'RecruiterManager', 'Developer']);
  const canDelete = isSuperAdmin();
  const canEmail  = hasRole(['SuperAdmin', 'RecruiterManager']);
  const canFilterByRecruiter = hasRole(['SuperAdmin', 'RecruiterManager']);

  // Always returns an array — guards against legacy string values from DB
  const toSkillsArray = (v) =>
    Array.isArray(v) ? v
    : (typeof v === 'string' && v.trim() ? v.split(',').map(s => s.trim()).filter(Boolean) : []);

  const [requirements, setRequirements] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [total, setTotal]               = useState(0);
  const [pages, setPages]               = useState(1);
  const [page, setPage]                 = useState(1);
  // Search and filters are kept for this tab (survive leaving the page).
  const [search, setSearch]             = useState(() => readPersisted('requirements:search', ''));
  const [statusFilter, setStatusFilter] = usePersistentState('requirements:status', '');
  const [clientFilter, setClientFilter] = usePersistentState('requirements:client', '');
  // "Recruiter" filter — who raised the requirement. SuperAdmin and
  // RecruiterManager only.
  const [recruiterFilter, setRecruiterFilter] = usePersistentState('requirements:recruiter', '');
  const [authors, setAuthors]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modalOpen, setModalOpen]       = useState(false);
  const [editReq, setEditReq]           = useState(null);
  const [form, setForm]                 = useState(emptyForm);
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState(null);
  const [clients, setClients]           = useState([]);
  const [teams, setTeams]               = useState([]);
  // Batch-submission email templates (client-facing — a {{candidates_table}} or a
  // {{#candidates}} loop). Used by the per-requirement "Batch email format" selector.
  const [batchTemplates, setBatchTemplates] = useState([]);
  const [matchedModal, setMatchedModal] = useState({ open: false, requirement: null, candidates: [], loading: false });
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  // ← View-only popup (opened from email deep-link)
  const [viewModal, setViewModal]   = useState({ open: false, req: null });
  // ← NEW: contact detail popup
  const [contactModal, setContactModal] = useState({ open: false, requirement: null });
  // ← Per-entity history panel
  const [historyPanel, setHistoryPanel] = useState({ open: false, id: null, name: '' });
  // ← Global requirement history feed
  const [globalHistory, setGlobalHistory] = useState(false);

  // Search box value vs. the debounced term the list is loaded with: typing
  // fires one request ~300ms after the last keystroke instead of one per key.
  const [searchInput, setSearchInput] = usePersistentState('requirements:search', '');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Only the latest list request may update the screen. Requests land on
  // different pods with different latency, so an earlier query can resolve
  // after a later one and would otherwise overwrite it.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const { data } = await requirementAPI.getAll({ page, limit: 6, search, status: statusFilter, clientId: clientFilter, recruiterId: recruiterFilter });
      if (seq !== loadSeq.current) return;
      // Normalize requiredSkills — some legacy records come back as a comma-separated string
      const normalized = (data.requirements || []).map(r => ({
        ...r,
        requiredSkills: Array.isArray(r.requiredSkills)
          ? r.requiredSkills
          : (typeof r.requiredSkills === 'string' && r.requiredSkills.trim()
              ? r.requiredSkills.split(',').map(s => s.trim()).filter(Boolean)
              : []),
      }));
      setRequirements(normalized);
      setTotal(data.total);
      setPages(data.pages);
      // Filter options ride along with the list — see utils/authorOptions.
      setAuthors(data.authors || []);
    } catch {
      if (seq === loadSeq.current) toast.error('Failed to load requirements');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [page, search, statusFilter, clientFilter, recruiterFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    clientAPI.dropdown().then(({ data }) => setClients(data.clients)).catch(() => {});
    teamAPI.getAll({ limit: 100 }).then(({ data }) => setTeams(data.teams)).catch(() => {});
    // Batch-submission templates only: client-facing (or both) AND containing the
    // {{candidates_table}} variable — the exact set the Batch-to-Client tab uses.
    emailAPI.getTemplates({ showAll: true })
      .then(({ data }) => {
        const hasBatchVar = (t) => {
          const body = t.body || '';
          const hasTable = Array.isArray(t.variables) ? t.variables.includes('candidates_table') : body.includes('{{candidates_table}}');
          return hasTable || body.includes('{{#candidates}}');
        };
        const list = (data.templates || []).filter(t =>
          (t.targetAudience === 'client' || t.targetAudience === 'both') && hasBatchVar(t)
        );
        setBatchTemplates(list);
      })
      .catch(() => setBatchTemplates([]));
  }, []);

  // Deep-link: auto-open requirement when ?id=<requirementId> is in the URL
  useEffect(() => {
    const targetId = searchParams.get('id');
    if (!targetId || deepLinkHandled.current || requirements.length === 0) return;

    const match = requirements.find(r => String(r.id) === String(targetId));
    if (match) {
      deepLinkHandled.current = true;
      setViewModal({ open: true, req: match });
      // Remove ?id= from URL so a refresh doesn't re-open the modal
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('id');
        return next;
      }, { replace: true });
    }
  }, [requirements, searchParams, setSearchParams]);

  const openCreate = () => { setEditReq(null); setForm(emptyForm); setModalOpen(true); };

  const openEdit = (r) => {
    setEditReq(r);
    const initialForm = {
      jobTitle:        r.jobTitle,
      jobDescription:  r.jobDescription || '',
      requiredSkills:  Array.isArray(r.requiredSkills) ? r.requiredSkills.join(', ') : '',
      minExperience:   r.minExperience,
      maxExperience:   r.maxExperience,
      clientId:        r.clientId || r.client?.id || '',
      budgetMin:       r.budgetMin || '',
      budgetMax:       r.budgetMax || '',
      budgetCurrency:  r.budgetCurrency || 'USD',
      location:        r.location || '',
      jobType:         r.jobType || 'Full-time',
      workMode:        r.workMode || 'On-site',
      status:          r.status,
      priority:        r.priority,
      openings:        r.openings,
      billingCost:     r.billingCost || '',
      billingCurrency: r.billingCurrency || 'USD',
      billingType:     r.billingType || 'Fixed',
      contacts:          Array.isArray(r.contacts) ? r.contacts : [],
      teamId:            r.teamId || r.assignedTeam?.id || '',
      description:       r.description || '',
      candidateLocation: r.candidateLocation || '',
      jobLocation:       r.jobLocation || '',
      closeBy:           r.closeBy ? r.closeBy.slice(0, 10) : '',
      shiftTime:         r.shiftTime || '',
      batchTemplateId:   r.batchTemplateId || '',
      defaultCcEmail:    r.defaultCcEmail || '',
      clientOfficeAddress: r.clientOfficeAddress || '',
      forceInterviewEmail: !!r.forceInterviewEmail,
    };
    originalFormSnap.current = JSON.stringify(initialForm);
    setForm(initialForm);
    setModalOpen(true);
  };

  // ── Contact helpers ───────────────────────────────────────────────────────
  const addContact    = () => setForm(prev => ({ ...prev, contacts: [...prev.contacts, { ...emptyContact }] }));
  const removeContact = (i) => setForm(prev => ({ ...prev, contacts: prev.contacts.filter((_, idx) => idx !== i) }));
  const updateContact = (i, field, value) => setForm(prev => {
    const contacts = [...prev.contacts];
    contacts[i] = { ...contacts[i], [field]: value };
    return { ...prev, contacts };
  });

  const handleSave = async (e) => {
    e.preventDefault();

    // Guard: skip update if nothing changed
    if (editReq && originalFormSnap.current === JSON.stringify(form)) {
      toast('No changes to save.', { icon: 'ℹ️' });
      return;
    }

    setSaving(true);
    const payload = { ...form };
    try {
      if (editReq) {
        await requirementAPI.update(editReq.id, payload);
        toast.success('Requirement updated!');
        // Optimistic update — normalize requiredSkills back to array
        const optimisticPayload = { ...payload, requiredSkills: toSkillsArray(payload.requiredSkills) };
        setRequirements(prev => prev.map(r => r.id === editReq.id ? { ...r, ...optimisticPayload } : r));
      } else {
        await requirementAPI.create(payload);
        toast.success('Requirement created!');
        load(); // Re-fetch for new requirement (need server-generated ID + associations)
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => { setConfirmDelete(id); };

  const handleDeleteConfirmed = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    setDeleting(id);
    try { await requirementAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const handleStatusChange = async (req, newStatus) => {
    setUpdatingStatusId(req.id);
    try {
      await requirementAPI.update(req.id, { status: newStatus });
      toast.success(`Marked as "${newStatus}"`);
      // Optimistic update
      setRequirements(prev => prev.map(r => r.id === req.id ? { ...r, status: newStatus } : r));
    } catch { toast.error('Failed to update status'); }
    finally { setUpdatingStatusId(null); }
  };

  const loadMatches = async (req) => {
    setMatchedModal({ open: true, requirement: req, candidates: [], loading: true });
    try {
      const { data } = await requirementAPI.getMatchedCandidates(req.id);
      setMatchedModal({ open: true, requirement: req, candidates: data.candidates, loading: false });
    } catch {
      toast.error('Failed to load matches');
      setMatchedModal(prev => ({ ...prev, loading: false }));
    }
  };

  // ── Scroll-lock: prevent body scroll when any overlay is open ────────────
  const anyOverlayOpen = modalOpen || viewModal.open || contactModal.open || matchedModal.open || historyPanel.open || globalHistory || !!confirmDelete;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Requirements</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{total} total requirements</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setGlobalHistory(true)}
            tooltip="View all requirement changes"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            All History
          </Button>
          {canCreate && <Button onClick={openCreate}>+ Add Requirement</Button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input placeholder="Search job title..." value={searchInput}
          onChange={e => { setSearchInput(e.target.value); setPage(1); }} className="w-full sm:w-auto sm:max-w-xs" />
        <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="w-full sm:w-36">
          <option value="">All Status</option>
          <option>Open</option><option>Closed</option><option>On Hold</option>
        </Select>
        <SearchableSelect
          value={clientFilter}
          onChange={(val) => { setClientFilter(val); setPage(1); }}
          placeholder="All Clients"
          className="w-full sm:w-52"
          options={clients.map(c => ({ value: c.id, label: c.clientName, sub: c.industry || '' }))}
        />
        {/* Recruiter filter — who raised the requirement. Options come from the
            list response, so they match the names actually on the page. */}
        {canFilterByRecruiter && authors.length > 0 && (
          <SearchableSelect
            value={recruiterFilter}
            onChange={(val) => { setRecruiterFilter(val); setPage(1); }}
            placeholder="All Recruiters"
            className="w-full sm:w-52"
            options={authorOptions(authors)}
          />
        )}
      </div>

      {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : requirements.length === 0 ? (
        <EmptyState icon="📋" title="No requirements found" message="Add your first job requirement."
          action={canCreate && <Button onClick={openCreate}>Add Requirement</Button>} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {requirements.map(r => (
            <div
              key={r.id}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 14, padding: 20,
                display: 'flex', flexDirection: 'column',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                boxShadow: 'var(--shadow-card)',
                cursor: 'pointer',
              }}
              onClick={() => setViewModal({ open: true, req: r })}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-brand)'; e.currentTarget.style.boxShadow = isDark ? '0 4px 20px rgba(0,0,0,0.25)' : '0 4px 16px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
            >
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="min-w-0">
                  <h3 className="break-words" style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{r.jobTitle}</h3>
                  <p className="break-words" style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>🏢 {r.client?.clientName}</p>
                  <p style={{ fontSize: 10, color: isDark ? '#6366f1' : '#818cf8', marginTop: 3, letterSpacing: '0.02em' }}>Click to view details →</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                  <Badge variant={statusVariant(r.priority)} size="sm">{r.priority}</Badge>
                </div>
              </div>

              {/* Description */}
              {r.description && (
                <SafeHtml as="div" html={r.description} className="line-clamp-2"
                  style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6, fontStyle: 'italic', borderLeft: '2px solid rgba(99,102,241,0.3)', paddingLeft: 8 }} />
              )}

              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }} className="space-y-1">
                <p>💼 {r.minExperience}–{r.maxExperience} years</p>
                {r.location && <p>📍 {r.location}</p>}
                {(r.candidateLocation || r.jobLocation) && (
                  <p className="flex items-center gap-1">
                    <span>🗺</span>
                    {r.candidateLocation && <span>Candidate: {r.candidateLocation}</span>}
                    {r.candidateLocation && r.jobLocation && <span style={{ color: 'var(--text-muted)' }}>·</span>}
                    {r.jobLocation && <span>Job: {r.jobLocation}</span>}
                  </p>
                )}
                {r.budgetMin && !isDeveloper && <p>💰 Budget: Min {fmtLPA(r.budgetMin)}{fmtLPA(r.budgetMax) ? ` · Max ${fmtLPA(r.budgetMax)}` : ''}</p>}
                <p>
                  🧑‍💼 {r.jobType} · {r.openings} opening{r.openings !== 1 ? 's' : ''}
                  {r.workMode && (
                    <span style={{
                      marginLeft: 6, padding: '1px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600,
                      ...(r.workMode === 'Remote'
                        ? { background: isDark ? 'rgba(16,185,129,0.18)' : '#dcfce7', color: isDark ? '#6ee7b7' : '#16a34a', border: `1px solid ${isDark ? 'rgba(16,185,129,0.25)' : '#bbf7d0'}` }
                        : r.workMode === 'Hybrid'
                        ? { background: isDark ? 'rgba(59,130,246,0.18)' : '#dbeafe', color: isDark ? '#93c5fd' : '#2563eb', border: `1px solid ${isDark ? 'rgba(59,130,246,0.25)' : '#bfdbfe'}` }
                        : { background: isDark ? 'rgba(100,116,139,0.3)' : '#f1f5f9', color: isDark ? '#cbd5e1' : '#475569', border: `1px solid ${isDark ? 'rgba(100,116,139,0.3)' : '#e2e8f0'}` }
                      )
                    }}>{r.workMode}</span>
                  )}
                </p>
                {r.shiftTime && <p>🕐 Shift: {r.shiftTime}</p>}
                {r.billingCost && !isDeveloper && (
                  <p style={{ color: '#d97706', fontWeight: 500 }}>
                    🧾 Billing: {formatBilling(r.billingCost, r.billingCurrency, r.billingType)}
                    <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>({r.billingType})</span>
                  </p>
                )}
                {r.closeBy && (() => {
                  const today = new Date();
                  today.setHours(0,0,0,0);
                  const due = new Date(r.closeBy);
                  const diffDays = Math.ceil((due - today) / 86400000);
                  const overdue  = diffDays < 0;
                  const urgent   = diffDays >= 0 && diffDays <= 3;
                  return (
                    <p style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500, color: overdue ? '#ef4444' : urgent ? '#f59e0b' : 'var(--text-secondary)' }}>
                      🗓 Close By: {due.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {overdue && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 99, background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)' }}>Overdue</span>}
                      {urgent && !overdue && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 99, background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}>Due soon</span>}
                    </p>
                  );
                })()}
              </div>

              {toSkillsArray(r.requiredSkills).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {toSkillsArray(r.requiredSkills).slice(0, 4).map(s => (
                    <span key={s} style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 500,
                      background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
                      color: isDark ? '#a5b4fc' : '#4f46e5',
                      border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#c7d2fe'}`,
                    }}>{s}</span>
                  ))}
                  {toSkillsArray(r.requiredSkills).length > 4 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>+{toSkillsArray(r.requiredSkills).length - 4}</span>}
                </div>
              )}

              {/* ── Contact persons badge ── */}
              {r.contacts?.length > 0 && (
                <Button
                  variant="success"
                  size="xs"
                  onClick={e => { e.stopPropagation(); setContactModal({ open: true, requirement: r }); }}
                  style={{ marginBottom: 10, alignSelf: 'flex-start', borderRadius: 99 }}
                >
                  👤 {r.contacts.length} Contact{r.contacts.length !== 1 ? 's' : ''}
                </Button>
              )}

              {/* ── Assigned Team badge ── */}
              {r.assignedTeam && (
                <div style={{ marginBottom: 10 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500,
                    background: isDark ? 'rgba(139,92,246,0.12)' : '#faf5ff',
                    color: isDark ? '#c4b5fd' : '#7c3aed',
                    border: `1px solid ${isDark ? 'rgba(139,92,246,0.2)' : '#ddd6fe'}`,
                    padding: '4px 10px', borderRadius: 99,
                  }}>
                    🏆 {r.assignedTeam.name}
                  </span>
                </div>
              )}

              {/* ── Actions ── */}
              <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, marginTop: 'auto' }} className="space-y-2" onClick={e => e.stopPropagation()}>
                {/* Inline status toggle */}
                {canCreate && (
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>Status:</span>
                    <div className="flex gap-1">
                      {['Open', 'Closed'].map(s => (
                        <Button key={s}
                          size="xs"
                          disabled={updatingStatusId === r.id}
                          onClick={() => handleStatusChange(r, s)}
                          variant={r.status === s ? (s === 'Open' ? 'success' : 'danger') : 'ghost'}
                          style={{ borderRadius: 99 }}
                        >
                          {updatingStatusId === r.id && r.status !== s ? '…' : s}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 flex-wrap">
                  <Button variant="success" size="xs" onClick={() => loadMatches(r)}>🎯 Match</Button>
                  <Button variant="outline" size="xs" onClick={() => setHistoryPanel({ open: true, id: r.id, name: r.jobTitle })} tooltip="View activity history for this requirement">🕐 History</Button>
                  {canEmail && (
                    <Button variant="primary" size="xs"
                      onClick={() => navigate('/emails', { state: { compose: 'candidate', requirement: r } })}
                      tooltip="Compose email to candidates for this requirement"
                    >✉ Email Candidate</Button>
                  )}
                  {(canCreate && (!isDeveloper || (r.createdById === user?.id && r.createdByRole === 'Developer'))) && (
                    <Button variant="secondary" size="xs" onClick={() => openEdit(r)}>✏️ Edit</Button>
                  )}
                  {(canDelete || (isDeveloper && r.createdById === user?.id && r.createdByRole === 'Developer')) && (
                    <div style={{ marginLeft: 'auto' }} onClick={(e) => e.stopPropagation()}><Button variant="danger" size="xs" loading={deleting === r.id} loadingText="Deleting…" onClick={() => handleDelete(r.id)}>Delete</Button></div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} pages={pages} onPage={setPage} />

      {/* ── View Requirement Modal (deep-link / email CTA) ─────────────────── */}
      {viewModal.open && viewModal.req && (() => {
        const r = viewModal.req;
        const closeView = () => setViewModal({ open: false, req: null });
        const today = new Date(); today.setHours(0,0,0,0);
        const dueDays = r.closeBy ? Math.ceil((new Date(r.closeBy) - today) / 86400000) : null;

        const Section = ({ title, children }) => (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? '#818cf8' : '#6366f1', marginBottom: 10 }}>{title}</p>
            {children}
          </div>
        );

        const Field = ({ label, value, wide }) => value ? (
          <div style={{ gridColumn: wide ? '1 / -1' : undefined }}>
            <p style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, fontWeight: 500 }}>{label}</p>
            <p style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{value}</p>
          </div>
        ) : null;

        return (
          <Modal isOpen={viewModal.open} onClose={closeView} title="Requirement Details" size="lg">
            <div style={{ maxHeight: 'min(72vh, 600px)', overflowY: 'auto', paddingRight: 4 }}>

              {/* Header strip */}
              <div style={{ background: isDark ? 'rgba(99,102,241,0.1)' : '#eef2ff', border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#c7d2fe'}`, borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{r.jobTitle}</h2>
                  {r.client?.clientName && <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>🏢 {r.client.clientName}</p>}
                  {r.assignedTeam?.name && <p style={{ fontSize: 12, color: isDark ? '#c4b5fd' : '#7c3aed', marginTop: 2 }}>🏆 {r.assignedTeam.name}</p>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                  <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                  <Badge variant={statusVariant(r.priority)} size="sm">{r.priority}</Badge>
                </div>
              </div>

              {/* Description */}
              {r.description && (
                <div style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa', border: '1px solid var(--border-subtle)', borderLeft: '3px solid #6366f1', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? '#818cf8' : '#6366f1', marginBottom: 6 }}>Description</p>
                  <SafeHtml as="div" html={r.description} style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }} />
                </div>
              )}

              {/* Core details grid */}
              <Section title="Role Details">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <Field label="Experience" value={`${r.minExperience} – ${r.maxExperience} years`} />
                  <Field label="Openings" value={`${r.openings} opening${r.openings !== 1 ? 's' : ''}`} />
                  <Field label="Job Type" value={r.jobType} />
                  <Field label="Work Mode" value={r.workMode} />
                  <Field label="Location" value={r.location} />
                  <Field label="Candidate Location" value={r.candidateLocation} />
                  <Field label="Job Location" value={r.jobLocation} />
                  <Field label="Shift Time" value={r.shiftTime} />
                  {dueDays !== null && (
                    <div>
                      <p style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, fontWeight: 500 }}>Close By</p>
                      <p style={{ fontSize: 13, fontWeight: 600, color: dueDays < 0 ? '#ef4444' : dueDays <= 3 ? '#f59e0b' : 'var(--text-primary)' }}>
                        {new Date(r.closeBy).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {dueDays < 0 && <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', borderRadius: 99, background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)' }}>Overdue</span>}
                        {dueDays >= 0 && dueDays <= 3 && <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', borderRadius: 99, background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}>Due soon</span>}
                      </p>
                    </div>
                  )}
                </div>
              </Section>

              {/* Skills */}
              {toSkillsArray(r.requiredSkills).length > 0 && (
                <Section title="Required Skills">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {toSkillsArray(r.requiredSkills).map(s => (
                      <span key={s} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 99, fontWeight: 500, background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff', color: isDark ? '#a5b4fc' : '#4f46e5', border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#c7d2fe'}` }}>{s}</span>
                    ))}
                  </div>
                </Section>
              )}

              {/* Budget & Billing — hidden for Developer role */}
              {!isDeveloper && (r.budgetMin || r.billingCost) && (
                <Section title="Compensation & Billing">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {r.budgetMin && <Field label="Budget" value={`Min ${fmtLPA(r.budgetMin)}${fmtLPA(r.budgetMax) ? ` · Max ${fmtLPA(r.budgetMax)}` : ''}`} />}
                    {r.billingCost && <Field label="Billing Cost" value={`${formatBilling(r.billingCost, r.billingCurrency, r.billingType)} (${r.billingType})`} />}
                  </div>
                </Section>
              )}

              {/* Job Description */}
              {/* jobDescription is HTML once edited via RTE; legacy plain text
                  still renders fine because SafeHtml escapes nothing extra. */}
              {r.jobDescription && (
                <Section title="Job Description">
                  <div style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '12px 16px' }}>
                    <SafeHtml html={r.jobDescription}
                      style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.75 }} />
                  </div>
                </Section>
              )}

              {/* Contacts */}
              {r.contacts?.length > 0 && (
                <Section title={`Contact Persons (${r.contacts.length})`}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {r.contacts.map((c, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '10px 14px' }}>
                        <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                          {c.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.name || '—'}</p>
                          {c.email && <a href={`mailto:${c.email}`} style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</a>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

            </div>

            {/* Footer actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 16, borderTop: '1px solid var(--border-subtle)', marginTop: 4 }}>
              {(canCreate && (!isDeveloper || (r.createdById === user?.id && r.createdByRole === 'Developer'))) && (
                <Button variant="secondary" onClick={() => { closeView(); openEdit(r); }}>✏️ Edit</Button>
              )}
              <Button onClick={closeView}>Close</Button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Create / Edit Modal ────────────────────────────────────────────── */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editReq ? 'Edit Requirement' : 'Add Requirement'} size="lg">
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Job Title *" value={form.jobTitle}
              onChange={e => setForm({ ...form, jobTitle: e.target.value })} required className="col-span-2" />
            <SearchableSelect
              label="Client *"
              required
              value={form.clientId}
              onChange={(val) => setForm(p => ({ ...p, clientId: val }))}
              placeholder="— Select Client —"
              options={clients.map(c => ({
                value: c.id,
                label: c.clientName,
                sub:   c.industry || '',
              }))}
            />
            <Select label="Assign to Team" value={form.teamId}
              onChange={e => setForm({ ...form, teamId: e.target.value })}>
              <option value="">-- No Team --</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
            <Input label="Location" value={form.location}
              onChange={e => setForm({ ...form, location: e.target.value })} />
            <Input label="Min Experience (yrs) *" type="text" inputMode="decimal" value={form.minExperience}
              onChange={e => setForm({ ...form, minExperience: onlyDecimal(e.target.value) })} required />
            <Input label="Max Experience (yrs) *" type="text" inputMode="decimal" value={form.maxExperience}
              onChange={e => setForm({ ...form, maxExperience: onlyDecimal(e.target.value) })} required />
            {/* Budget Min with inline currency — hidden for Developer role */}
            {!isDeveloper && (
              <div className="flex flex-col gap-1.5">
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Budget Min (LPA)</label>
                <div className="flex items-stretch gap-0" style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', background: 'var(--input-bg)' }}>
                  <select
                    value={form.budgetCurrency}
                    onChange={e => setForm({ ...form, budgetCurrency: e.target.value })}
                    style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: 'none', borderRight: '1px solid var(--border-subtle)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, padding: '0 10px', cursor: 'pointer', outline: 'none', minWidth: 60 }}
                  >
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input type="text" inputMode="decimal" value={form.budgetMin}
                    onChange={e => setForm({ ...form, budgetMin: onlyDecimal(e.target.value) })}
                    placeholder="e.g. 50000"
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 12px' }} />
                </div>
              </div>
            )}

            {/* Budget Max with inline currency (shares budgetCurrency) — hidden for Developer role */}
            {!isDeveloper && (
              <div className="flex flex-col gap-1.5">
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Budget Max (LPA)</label>
                <div className="flex items-stretch gap-0" style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', background: 'var(--input-bg)' }}>
                  <div style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderRight: '1px solid var(--border-subtle)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, padding: '0 10px', minWidth: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {form.budgetCurrency || 'USD'}
                  </div>
                  <input type="text" inputMode="decimal" value={form.budgetMax}
                    onChange={e => setForm({ ...form, budgetMax: onlyDecimal(e.target.value) })}
                    placeholder="e.g. 80000"
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, padding: '9px 12px' }} />
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Enter in LPA (Lakhs Per Annum). A raw amount is auto-read as LPA — e.g. 1500000 shows as 15 LPA.
                </span>
              </div>
            )}
            <Select label="Job Type" value={form.jobType}
              onChange={e => setForm({ ...form, jobType: e.target.value })}>
              {JOB_TYPES.map(t => <option key={t}>{t}</option>)}
            </Select>
            <Select label="Work Mode" value={form.workMode}
              onChange={e => setForm({ ...form, workMode: e.target.value })}>
              {WORK_MODES.map(m => <option key={m}>{m}</option>)}
            </Select>
            <Input label="Openings" type="text" inputMode="numeric" value={form.openings}
              onChange={e => setForm({ ...form, openings: onlyDigits(e.target.value) })} />
            <Select label="Status" value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}>
              <option>Open</option><option>Closed</option><option>On Hold</option>
            </Select>
            <Select label="Priority" value={form.priority}
              onChange={e => setForm({ ...form, priority: e.target.value })}>
              <option>Low</option><option>Medium</option><option>High</option><option>Urgent</option>
            </Select>
            <Input label="Required Skills (comma-separated)" value={form.requiredSkills}
              onChange={e => setForm({ ...form, requiredSkills: e.target.value })}
              placeholder="react, node, postgresql..." className="col-span-2" />
          </div>

          {/* ── Description / Location / Close By ─────────────────────────── */}
          <div
            className="rounded-xl p-4 space-y-4"
            style={{ background: isDark ? 'rgba(99,102,241,0.05)' : '#f8faff', border: `1px solid ${isDark ? 'rgba(99,102,241,0.15)' : '#e0e7ff'}` }}
          >
            <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Additional Details</p>

            <RichTextEditor
              label="Description"
              value={form.description}
              onChange={(html) => setForm({ ...form, description: html })}
              placeholder="Internal notes or summary about this requirement…"
              rows={4}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Candidate Location"
                value={form.candidateLocation}
                onChange={e => setForm({ ...form, candidateLocation: e.target.value })}
                placeholder="e.g. Hyderabad"
              />
              <Input
                label="Job Location"
                value={form.jobLocation}
                onChange={e => setForm({ ...form, jobLocation: e.target.value })}
                placeholder="e.g. Bengaluru"
              />
            </div>

            <Input
              label="Close By (last date to fill this requirement)"
              type="date"
              value={form.closeBy}
              onChange={e => setForm({ ...form, closeBy: e.target.value })}
            />

            <Input
              label="Shift Time"
              value={form.shiftTime}
              onChange={e => setForm({ ...form, shiftTime: e.target.value })}
              placeholder="e.g. 9am to 6pm IST, 4pm to 1am EST"
            />

          </div>

          {/* ── Billing Cost — hidden for Developer role ── */}
          {!isDeveloper && (
            <div>
              <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 8 }}>Billing Cost <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(charged to client for this placement)</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                  label="Amount"
                  type="text"
                  inputMode="decimal"
                  value={form.billingCost}
                  onChange={e => setForm({ ...form, billingCost: onlyDecimal(e.target.value) })}
                  placeholder="e.g. 5000"
                />
                <Select
                  label="Currency"
                  value={form.billingCurrency}
                  onChange={e => setForm({ ...form, billingCurrency: e.target.value })}
                >
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
                <Select
                  label="Type"
                  value={form.billingType}
                  onChange={e => setForm({ ...form, billingType: e.target.value })}
                >
                  {BILLING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
              </div>
              {form.billingCost && (
                <p style={{ fontSize: 11, color: '#d97706', marginTop: 6 }}>
                  Preview: <span style={{ fontWeight: 600 }}>{formatBilling(form.billingCost, form.billingCurrency, form.billingType)}</span>
                  {' '}— this will be available as <span style={{ fontFamily: 'monospace', background: 'var(--bg-raised)', padding: '1px 4px', borderRadius: 4, border: '1px solid var(--border-subtle)' }}>{'{{billing_cost}}'}</span> in client email templates.
                </p>
              )}
            </div>
          )}

          <RichTextEditor label="Job Description" value={form.jobDescription} rows={8}
            placeholder="Role responsibilities, requirements, perks…"
            onChange={(html) => setForm({ ...form, jobDescription: html })} />

          {/* ── Contact Persons section ─────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Contact Persons</label>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>Client-side POC for this role</span>
              </div>
              <Button type="button" variant="outline" size="xs" onClick={addContact}>+ Add Contact</Button>
            </div>

            {form.contacts.length === 0 ? (
              <div style={{ border: `2px dashed ${isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff'}`, borderRadius: 10, padding: '20px 16px', textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No contacts added</p>
                <Button type="button" variant="ghost" size="xs" onClick={addContact} style={{ marginTop: 4 }}>Add first contact</Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="hidden sm:grid grid-cols-2 gap-2 pr-10">
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', paddingLeft: 4 }}>Name</p>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', paddingLeft: 4 }}>Email</p>
                </div>
                {form.contacts.map((contact, i) => (
                  <ContactRow key={i} contact={contact} index={i}
                    onChange={updateContact} onRemove={removeContact} />
                ))}
              </div>
            )}
          </div>
          {/* ─────────────────────────────────────────────────────────────────── */}

          {/* ── Client Office Address ────────────────────────────────────────── */}
          <div style={{ background: isDark ? 'rgba(99,102,241,0.04)' : '#f8fafc', borderRadius: 12, padding: 16 }}>
            <div className="flex items-center mb-3">
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Client Office Address</label>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                  Where the client's office is located. Separate from Job Location.
                </span>
              </div>
            </div>
            <RichTextEditor
              value={form.clientOfficeAddress}
              rows={4}
              onChange={(html) => setForm({ ...form, clientOfficeAddress: html })}
              placeholder="Full address — street, city, state, country, zip…"
            />
          </div>

          {/* ── Default CC Emails ────────────────────────────────────────────── */}
          <div style={{ background: isDark ? 'rgba(99,102,241,0.04)' : '#f8fafc', borderRadius: 12, padding: 16 }}>
            <div className="flex items-center mb-3">
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Default CC Emails</label>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                  Auto-added to CC when sending client emails for this requirement
                </span>
              </div>
            </div>
            <EmailTagInput
              value={form.defaultCcEmail}
              onChange={(v) => setForm({ ...form, defaultCcEmail: v })}
              placeholder="manager@client.com, hr@client.com"
              isDark={isDark}
            />
          </div>
          {/* ─────────────────────────────────────────────────────────────────── */}

          {/* ── Force interview invitation email (per-requirement) ───────────── */}
          <div style={{ background: isDark ? 'rgba(99,102,241,0.04)' : '#f8fafc', borderRadius: 12, padding: 16 }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Always send interview invitation email</label>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginTop: 3, maxWidth: 460, lineHeight: 1.5 }}>
                  When on, scheduling an interview for a candidate under this requirement auto-ticks “Send interview invitation email” and locks the candidate’s name &amp; email in the interview form. CC stays editable.
                </span>
              </div>
              <button type="button" role="switch" aria-checked={!!form.forceInterviewEmail}
                aria-label="Always send interview invitation email"
                onClick={() => setForm({ ...form, forceInterviewEmail: !form.forceInterviewEmail })}
                style={{
                  flexShrink: 0, width: 44, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer',
                  padding: 2, transition: 'background 0.15s', display: 'inline-flex', alignItems: 'center',
                  background: form.forceInterviewEmail ? '#6366f1' : (isDark ? '#3f3f46' : '#cbd5e1'),
                }}>
                <span style={{
                  width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'block',
                  transform: form.forceInterviewEmail ? 'translateX(20px)' : 'translateX(0)',
                  transition: 'transform 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                }} />
              </button>
            </div>
          </div>
          {/* ─────────────────────────────────────────────────────────────────── */}

          {/* ── Batch Email Format (per-requirement) ─────────────────────────── */}
          <div style={{ background: isDark ? 'rgba(99,102,241,0.04)' : '#f8fafc', borderRadius: 12, padding: 16 }}>
            <div className="mb-2">
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Batch Email Format</label>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                Auto-selected when composing a Batch-to-Client email for this requirement
              </span>
            </div>
            <SearchableSelect
              value={form.batchTemplateId}
              onChange={(val) => setForm(p => ({ ...p, batchTemplateId: val }))}
              placeholder="— No specific format (choose manually when sending) —"
              options={[
                { value: '', label: 'No specific format (choose manually)' },
                ...batchTemplates.map(t => ({ value: t.id, label: t.name, sub: t.subject || '' })),
              ]}
            />
            {form.batchTemplateId && !batchTemplates.some(t => t.id === form.batchTemplateId) && (
              <p style={{ fontSize: 11, color: '#d97706', marginTop: 6 }}>
                ⚠ The saved format template is no longer available. Pick another, or leave as manual.
              </p>
            )}
          </div>
          {/* ─────────────────────────────────────────────────────────────────── */}

          <div className="flex flex-wrap gap-3 justify-end">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>{editReq ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      {/* ── Contact Detail Modal ────────────────────────────────────────────── */}
      <Modal isOpen={contactModal.open}
        onClose={() => setContactModal({ open: false, requirement: null })}
        title={`Contacts — ${contactModal.requirement?.jobTitle}`} size="sm">
        <div className="space-y-3">
          {(contactModal.requirement?.contacts || []).length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>No contacts added.</p>
          ) : (
            (contactModal.requirement?.contacts || []).map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                  {c.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.name || '—'}</p>
                  {c.email && (
                    <a href={`mailto:${c.email}`} style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.email}
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
          <div className="pt-2 flex justify-end">
            <Button variant="secondary" onClick={() => setContactModal({ open: false, requirement: null })}>Close</Button>
          </div>
        </div>
      </Modal>

      {/* ── Matched Candidates Modal ────────────────────────────────────────── */}
      <Modal isOpen={matchedModal.open}
        onClose={() => setMatchedModal({ open: false, requirement: null, candidates: [], loading: false })}
        title="🎯 Matched Candidates" size="xl">
        {matchedModal.requirement && (
          <div>
            <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <h3 style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{matchedModal.requirement.jobTitle}</h3>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                {matchedModal.requirement.minExperience}–{matchedModal.requirement.maxExperience} yrs ·
                Skills: {toSkillsArray(matchedModal.requirement.requiredSkills).join(', ')}
              </p>
            </div>

            {matchedModal.loading ? (
              <div className="py-12 flex justify-center"><Spinner /></div>
            ) : matchedModal.candidates.length === 0 ? (
              <EmptyState icon="🔍" title="No matches found" message="No candidates match the criteria." />
            ) : (
              <div className="space-y-3">
                {matchedModal.candidates.map((c, idx) => (
                  <div key={c.id}
                    onClick={() => navigate(`/candidate-profile/${c.id}`)}
                    style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 16, cursor: 'pointer', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#6366f1'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, position: 'relative', flexShrink: 0 }}>
                          {c.fullName?.[0]}
                          {idx < 3 && (
                            <span style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, background: '#f59e0b', borderRadius: '50%', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', fontWeight: 700 }}>
                              {idx + 1}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.fullName}</p>
                          <p style={{ fontSize: 11, color: 'var(--text-secondary)', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{c.email} · {c.experience} yrs</p>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#6366f1' }}>{c.matchDetails.matchScore}%</div>
                        <p style={{ fontSize: 10, color: 'var(--text-muted)' }}>Match</p>
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
                        <span>✅ Skills: {c.matchDetails.skillMatchPercent}%</span>
                        <span>{c.matchDetails.expInRange ? '✅' : '⚠️'} Experience</span>
                      </div>
                      <div style={{ width: '100%', background: isDark ? '#334155' : '#e2e8f0', borderRadius: 99, height: 6 }}>
                        <div style={{ background: '#6366f1', height: 6, borderRadius: 99, width: `${c.matchDetails.matchScore}%` }} />
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {c.matchDetails.matchedSkills.map(s => (
                          <span key={s} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: isDark ? 'rgba(34,197,94,0.15)' : '#dcfce7', color: isDark ? '#86efac' : '#16a34a', border: `1px solid ${isDark ? 'rgba(34,197,94,0.25)' : '#bbf7d0'}` }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Per-entity history panel ──────────────────────────────────────── */}
      {historyPanel.open && (
        <HistoryPanel
          entityId={historyPanel.id}
          entityName={historyPanel.name}
          onClose={() => setHistoryPanel({ open: false, id: null, name: '' })}
        />
      )}

      {/* ── Global requirement history feed ───────────────────────────────── */}
      {globalHistory && (
        <GlobalHistoryPanel
          module="Requirement"
          onClose={() => setGlobalHistory(false)}
        />
      )}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setConfirmDelete(null)} />
          <div style={{ position: 'relative', background: isDark ? '#1e2535' : '#ffffff', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>Delete Requirement?</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: isDark ? '#94a3b8' : '#6b7280' }}>This will permanently remove the requirement.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'transparent', color: isDark ? '#f1f5f9' : '#374151', cursor: 'pointer', fontWeight: 500, fontSize: 14 }}>Cancel</button>
              <button onClick={handleDeleteConfirmed} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
